import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowRight, Phone, MapPin, Star, Package } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_GALLERY = [
  { url: "https://images.pexels.com/photos/33650428/pexels-photo-33650428.jpeg", title: "Proceso de impresión" },
  { url: "https://images.pexels.com/photos/12025472/pexels-photo-12025472.jpeg", title: "Playera personalizada" },
  { url: "https://images.pexels.com/photos/8058800/pexels-photo-8058800.jpeg", title: "Artículos promocionales" },
];

export default function Landing() {
  const [products, setProducts] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    API.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    API.get("/gallery").then((r) => setGalleryItems(r.data)).catch(() => {});
    API.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const services = settings?.services || [
    { title: "Serigrafía Tradicional", desc: "Tintas plastisol y base agua para textiles" },
    { title: "Sublimación", desc: "Full color en poliéster y superficies" },
    { title: "DTF", desc: "Direct to Film, versatilidad total" },
    { title: "DTF UV", desc: "Impresión UV en superficies rígidas" },
    { title: "Transfer Térmico", desc: "Vinilo de corte e impresión" },
  ];
  const testimonials = settings?.testimonials || [
    { name: "María González", company: "Restaurante La Casona", text: "Excelente calidad en las playeras. Entrega puntual.", stars: 5 },
    { name: "Roberto Méndez", company: "Tech Solutions GT", text: "Uniformes corporativos perfectos. Muy profesionales.", stars: 5 },
    { name: "Ana Pérez", company: "Eventos Plus", text: "PYM es nuestra primera opción para artículos promocionales.", stars: 5 },
  ];
  const phone = settings?.phone || "+502 3779-2267";
  const location = settings?.location || "Guatemala";
  const description = settings?.description || "Impresión y artículos promocionales de calidad.";

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src="https://images.pexels.com/photos/9314012/pexels-photo-9314012.jpeg" alt="Serigrafía industrial" className="w-full h-full object-cover" />
          <div className="pym-hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-24">
          <div className="max-w-2xl opacity-0 animate-fade-in-up">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-4">PYM Promocionales</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#0F172A] leading-tight mb-6" style={{ fontFamily: 'Chivo' }}>
              Impresión y Artículos<br />Promocionales
            </h1>
            <p className="text-base text-[#475569] leading-relaxed mb-8 max-w-lg">
              Serigrafía, sublimación, DTF y más. Cotiza en minutos, recibe tu pedido a tiempo. Calidad garantizada para tu empresa.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/cotizar">
                <Button className="bg-[#E30613] text-white font-bold rounded-sm px-8 py-3 hover:bg-[#C80410] transition-colors text-sm" data-testid="hero-cta-btn">
                  Cotiza Ahora <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#servicios">
                <Button variant="outline" className="border-2 border-[#0047AB] text-[#0047AB] bg-transparent font-bold rounded-sm px-8 py-3 hover:bg-[#0047AB] hover:text-white transition-colors text-sm" data-testid="hero-services-btn">
                  Ver Servicios
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="servicios" className="py-20 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-3">Técnicas de Impresión</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A] mb-12" style={{ fontFamily: 'Chivo' }}>Nuestros Servicios</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#E2E8F0]">
            {services.map((s) => (
              <div key={s.title} className="p-8 border border-[#E2E8F0] hover:border-[#0047AB] transition-colors group" data-testid={`service-card-${s.title}`}>
                <Printer className="w-6 h-6 text-[#0047AB] mb-4 group-hover:text-[#E30613] transition-colors" />
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Chivo' }}>{s.title}</h3>
                <p className="text-sm text-[#475569]">{s.desc}</p>
              </div>
            ))}
            <div className="p-8 border border-[#E2E8F0] flex items-center justify-center">
              <Link to="/cotizar" className="text-[#0047AB] font-bold text-sm hover:text-[#E30613] transition-colors flex items-center gap-2">
                Ver todos los servicios <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-3">Catálogo</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A] mb-12" style={{ fontFamily: 'Chivo' }}>Artículos Disponibles</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map((p, i) => {
              const imgUrl = p.image_file_id ? `${API_BASE}/api/public/files/${p.image_file_id}` : null;
              return (
                <Link to="/cotizar" key={p.id || i} className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden hover:border-[#0047AB] transition-colors cursor-pointer group" data-testid={`product-card-${i}`}>
                  <div className="aspect-square bg-[#F1F5F9] flex items-center justify-center overflow-hidden">
                    {imgUrl ? (
                      <img src={imgUrl} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <Package className="w-10 h-10 text-[#CBD5E1]" />
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-semibold text-[#0F172A]">{p.name}</p>
                    <p className="text-xs text-[#475569] mt-0.5">Desde Q{p.base_price}</p>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link to="/cotizar">
              <Button className="bg-[#E30613] text-white font-bold rounded-sm px-8 py-3 hover:bg-[#C80410] transition-colors text-sm" data-testid="products-cta-btn">
                Cotizar Productos <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-white" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-3">Portafolio</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A] mb-12" style={{ fontFamily: 'Chivo' }}>Trabajos Realizados</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(galleryItems.length > 0 ? galleryItems : DEFAULT_GALLERY.map((g, i) => ({ id: `default-${i}`, ...g }))).map((g) => (
              <div key={g.id} className="border border-[#E2E8F0] rounded-sm overflow-hidden aspect-[4/3] group">
                <img
                  src={g.file_id ? `${API_BASE}/api/public/files/${g.file_id}` : g.url}
                  alt={g.title || "Trabajo realizado"}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-3">Testimonios</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A] mb-12" style={{ fontFamily: 'Chivo' }}>Lo Que Dicen Nuestros Clientes</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {testimonials.map((t) => (
              <div key={`${t.name}-${t.company}`} className="bg-white border border-[#E2E8F0] rounded-sm p-6" data-testid={`testimonial-${t.name}`}>
                <div className="flex gap-1 mb-4">
                  {[...Array(t.stars || 5)].map((_, j) => <Star key={`star-${j}`} className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />)}
                </div>
                <p className="text-sm text-[#475569] mb-4 leading-relaxed">"{t.text}"</p>
                <p className="text-sm font-semibold text-[#0F172A]">{t.name}</p>
                <p className="text-xs text-[#94A3B8]">{t.company}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0F172A]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Chivo' }}>
            ¿Listo para tu próximo proyecto?
          </h2>
          <p className="text-base text-[#94A3B8] mb-8 max-w-xl mx-auto">
            Cotiza en minutos. Sin compromiso, con precios transparentes y entrega puntual.
          </p>
          <Link to="/cotizar">
            <Button className="bg-[#E30613] text-white font-bold rounded-sm px-10 py-4 hover:bg-[#C80410] transition-colors text-base" data-testid="footer-cta-btn">
              Cotiza Ahora <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0B1120] text-[#94A3B8]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="https://customer-assets.emergentagent.com/job_promo-quote/artifacts/wgwlu5pd_WhatsApp%20Image%202026-03-27%20at%2018.43.52.jpeg" alt="PYM" className="h-8 w-auto" />
                <span className="font-black text-white text-sm" style={{ fontFamily: 'Chivo' }}>PYM Promocionales</span>
              </div>
              <p className="text-xs leading-relaxed">{description}</p>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-3" style={{ fontFamily: 'Chivo' }}>Contacto</h4>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2"><Phone className="w-3 h-3" /> {phone}</div>
                <div className="flex items-center gap-2"><MapPin className="w-3 h-3" /> {location}</div>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-3" style={{ fontFamily: 'Chivo' }}>Enlaces</h4>
              <div className="space-y-2 text-xs">
                <Link to="/cotizar" className="block hover:text-white transition-colors">Cotizador</Link>
                <Link to="/login" className="block hover:text-white transition-colors">Iniciar Sesión</Link>
                <Link to="/registro" className="block hover:text-white transition-colors">Registrarse</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-6 text-xs text-center">
            &copy; {new Date().getFullYear()} PYM Promocionales. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
